import {ExternalLinkIcon} from '@heroicons/react/outline';

import {Badge, Button, Link} from '@/components';
import {useCountryList} from '@/context/CountryListContext';
import {mapCountryCodeToFlags} from '@/utils/countries';
import * as dateUtils from '@/utils/date';
import {toSafeExternalUrl} from '@/utils/externalUrl';

import type {BusinessDetailsProps} from './types';

const BusinessDetails: React.FC<BusinessDetailsProps> = ({
  business,
  person,
}) => {
  const {state: countryListState} = useCountryList();
  const {countryList} = countryListState;

  const isApprovedForPostFunding =
    !!business?.meta?.flags?.isApprovedForPostFunding;
  const postFunding = business?.meta?.postFunding;
  const hasNegativeBalance = !!postFunding?.hasNegativeBalance;
  const businessWebsite = business?.meta?.kyb?.data?.BUSINESS_WEBSITE;
  // Merchant-supplied, so it is only linked when it is a plain http(s) URL.
  const businessWebsiteUrl = toSafeExternalUrl(businessWebsite);

  return (
    <div className="rounded-xl shadow-md border dark:border-dark-borderMid p-4 space-y-6 mb-6 mt-6">
      <div className="flex flex-col md:flex-row justify-between flex-wrap gap-6">
        <div className="w-full xl:flex-1">
          {/* --- Business Info --- */}
          <div className="space-y-2 self-center">
            <div className="py-2 px-3 flex justify-between bg-gray-100 dark:bg-gray-900 rounded gap-3">
              <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-200">
                Business Name
              </p>
              <p
                data-cy="business-details-business-name"
                className="flex-[0.6] text-sm font-medium w-1/2  truncate text-gray-700 dark:text-gray-100"
              >
                {business?.name || '-'}
              </p>
            </div>

            <div className="py-2 px-3 flex justify-between bg-gray-100 dark:bg-gray-900 rounded gap-3">
              <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-200">
                Country
              </p>
              <p
                data-cy="business-details-country"
                className="flex-[0.6] text-sm font-medium w-1/2  truncate text-gray-700 dark:text-gray-100"
              >
                {business?.countryCode
                  ? `${business.countryCode} ${mapCountryCodeToFlags(countryList)[business.countryCode]}`
                  : ''}
              </p>
            </div>
            <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
              <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
                Date Opened
              </p>
              <p className="flex-[0.6] text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100 ">
                {dateUtils.DDMonYYYY(business?.createdAt?.toString() || '')}
              </p>
            </div>
            {business?.organizationDomain && (
              <div className="py-2 px-3 flex justify-between bg-gray-100 dark:bg-gray-900 rounded gap-3">
                <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-200">
                  Organization Domain
                </p>
                <p
                  data-cy="business-details-organization-domain"
                  className="flex-[0.6] text-sm font-medium w-1/2  truncate text-gray-700 dark:text-gray-100"
                >
                  {business.organizationDomain}
                </p>
              </div>
            )}
            {businessWebsite && (
              <div className="py-2 px-3 flex justify-between bg-gray-100 dark:bg-gray-900 rounded gap-3">
                <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-200">
                  Business Website
                </p>
                <div className="flex-[0.6] text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100">
                  {businessWebsiteUrl ? (
                    <Link
                      to={businessWebsiteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button
                        variant="ghost"
                        className="no-padding no-margin hover:underline"
                        rightIcon={<ExternalLinkIcon className="w-4 h-4" />}
                      >
                        {businessWebsite}
                      </Button>
                    </Link>
                  ) : (
                    businessWebsite
                  )}
                </div>
              </div>
            )}
            <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
              <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
                Transactions
              </p>
              <p className="flex-[0.6] text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100 ">
                <Link
                  data-cy="business-details-transactions-link"
                  to={`/merchants/transactions/business/${business?.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    variant="ghost"
                    className="no-padding no-margin hover:underline"
                    rightIcon={<ExternalLinkIcon className="w-4 h-4" />}
                  >
                    View Transactions
                  </Button>
                </Link>
              </p>
            </div>
            <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
              <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
                KYB Details
              </p>
              <p className="flex-[0.6] text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100 ">
                <Link
                  data-cy="business-details-kyb-details"
                  to={`/merchants/kyb/${business?.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    variant="ghost"
                    className="no-padding no-margin hover:underline"
                    rightIcon={<ExternalLinkIcon className="w-4 h-4" />}
                  >
                    View KYB Details
                  </Button>
                </Link>
              </p>
            </div>
            <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
              <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
                UBO Details
              </p>
              <p className="flex-[0.6] text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100 ">
                <Link
                  data-cy="business-details-profile"
                  to={`/merchants/users/${person?.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    variant="ghost"
                    className="no-padding no-margin hover:underline"
                    rightIcon={<ExternalLinkIcon className="w-4 h-4" />}
                  >
                    View profile
                  </Button>
                </Link>
              </p>
            </div>
            <div className="py-2 px-3 flex justify-between bg-gray-100 dark:bg-gray-900 rounded gap-3">
              <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-200">
                Verified
              </p>
              <p className="flex-[0.6] text-sm font-medium w-1/2  truncate text-gray-700 dark:text-gray-100">
                <Badge
                  dataCy="business-details-verified-badge"
                  label={business?.meta?.flags?.isVerified ? 'Yes' : 'No'}
                  colorScheme={
                    business?.meta?.flags?.isVerified ? 'successful' : 'failed'
                  }
                  className="px-4"
                />
              </p>
            </div>
            <div className="py-2 px-3 flex justify-between bg-gray-100 dark:bg-gray-900 rounded gap-3">
              <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-200">
                Approved For API
              </p>
              <p className="flex-[0.6] text-sm font-medium w-1/2  truncate text-gray-700 dark:text-gray-100">
                <Badge
                  dataCy="business-details-approved-api-badge"
                  label={business?.meta?.flags?.isApprovedForAPI ? 'Yes' : 'No'}
                  colorScheme={
                    business?.meta?.flags?.isApprovedForAPI
                      ? 'successful'
                      : 'failed'
                  }
                  className="px-4"
                />
              </p>
            </div>
            <div className="py-2 px-3 flex justify-between bg-gray-100 dark:bg-gray-900 rounded gap-3">
              <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-200">
                Deactivated
              </p>
              <p className="flex-[0.6] text-sm font-medium w-1/2  truncate text-gray-700 dark:text-gray-100">
                <Badge
                  dataCy="business-details-deactivated-badge"
                  label={business?.meta?.flags?.isDeactivated ? 'Yes' : 'No'}
                  colorScheme={
                    business?.meta?.flags?.isDeactivated
                      ? 'failed'
                      : 'successful'
                  }
                  className="px-4"
                />
              </p>
            </div>
            <div className="py-2 px-3 flex justify-between bg-gray-100 dark:bg-gray-900 rounded gap-3">
              <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-200">
                Charge Fee via Wallet
              </p>
              <p className="flex-[0.6] text-sm font-medium w-1/2  truncate text-gray-700 dark:text-gray-100">
                <Badge
                  dataCy="business-details-charge-fee-via-wallet-badge"
                  label={
                    business?.meta?.flags?.isApprovedForFeeWallet ? 'Yes' : 'No'
                  }
                  colorScheme={
                    business?.meta?.flags?.isApprovedForFeeWallet
                      ? 'successful'
                      : 'failed'
                  }
                  className="px-4"
                />
              </p>
            </div>
            <div className="py-2 px-3 flex justify-between bg-gray-100 dark:bg-gray-900 rounded gap-3">
              <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-200">
                Approved For Post-Funding
              </p>
              <p className="flex-[0.6] text-sm font-medium w-1/2  truncate text-gray-700 dark:text-gray-100">
                <Badge
                  dataCy="business-details-approved-for-post-funding-badge"
                  label={isApprovedForPostFunding ? 'Yes' : 'No'}
                  colorScheme={
                    isApprovedForPostFunding ? 'successful' : 'failed'
                  }
                  className="px-4"
                />
              </p>
            </div>
            {/* Post-funding state only becomes meaningful once approved or once
                debt is outstanding (debt can persist after approval is
                revoked — see PostFundingConfigForm on the KYB page for
                editing the limit/rate). */}
            {(isApprovedForPostFunding || hasNegativeBalance) && (
              <>
                <div className="py-2 px-3 flex justify-between bg-gray-100 dark:bg-gray-900 rounded gap-3">
                  <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-200">
                    Post-Funding Limit
                  </p>
                  <p className="flex-[0.6] text-sm font-medium w-1/2  truncate text-gray-700 dark:text-gray-100">
                    ${business?.meta?.postFundingLimitUSD ?? 0}
                  </p>
                </div>
                <div className="py-2 px-3 flex justify-between bg-gray-100 dark:bg-gray-900 rounded gap-3">
                  <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-200">
                    Daily Interest Rate
                  </p>
                  <p className="flex-[0.6] text-sm font-medium w-1/2  truncate text-gray-700 dark:text-gray-100">
                    {business?.meta?.postFundingDailyInterestRatePercent ?? 0}%
                  </p>
                </div>
                <div className="py-2 px-3 flex justify-between bg-gray-100 dark:bg-gray-900 rounded gap-3">
                  <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-200">
                    Withdrawals Blocked
                  </p>
                  <p className="flex-[0.6] text-sm font-medium w-1/2  truncate text-gray-700 dark:text-gray-100">
                    <Badge
                      label={postFunding?.withdrawalsBlocked ? 'Yes' : 'No'}
                      colorScheme={
                        postFunding?.withdrawalsBlocked
                          ? 'failed'
                          : 'successful'
                      }
                      className="px-4"
                    />
                  </p>
                </div>
                {postFunding?.withdrawalsBlockedAt && (
                  <div className="py-2 px-3 flex justify-between bg-gray-100 dark:bg-gray-900 rounded gap-3">
                    <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-200">
                      Blocked Since
                    </p>
                    <p className="flex-[0.6] text-sm font-medium w-1/2  truncate text-gray-700 dark:text-gray-100">
                      {dateUtils.DDMonYYYY(
                        postFunding.withdrawalsBlockedAt.toString(),
                      )}
                    </p>
                  </div>
                )}
                {!!postFunding?.negativeCurrencies?.length && (
                  <div className="py-2 px-3 flex justify-between bg-gray-100 dark:bg-gray-900 rounded gap-3">
                    <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-200">
                      Negative Currencies
                    </p>
                    <p className="flex-[0.6] text-sm font-medium w-1/2  truncate text-gray-700 dark:text-gray-100">
                      {postFunding.negativeCurrencies.join(', ')}
                    </p>
                  </div>
                )}
                <div className="py-2 px-3 flex justify-between bg-gray-100 dark:bg-gray-900 rounded gap-3">
                  <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-200">
                    Total Interest Accrued
                  </p>
                  <p className="flex-[0.6] text-sm font-medium w-1/2  truncate text-gray-700 dark:text-gray-100">
                    ${(postFunding?.totalInterestAccruedUSD ?? 0).toFixed(2)}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessDetails;
